import { useToast } from '@chakra-ui/react';
import { CheckoutV2 } from '@getjust/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import type { Cart } from '@getjust/gateway';

import { useCustomRouter, useQueryError } from '$hooks/client';
import { useFormMessageAtom, useROEmailAtom, useROLoginAtom } from '$hooks/state';
import { isPrefilledAtom } from '$src/business/atoms/is-prefilled';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCart } from '$src/hooks/queries';
import { useShowChallengeAtom } from '$src/hooks/state/useShowChallengeAtom';
import { gateway } from '$src/http';

// Custom response type that includes cart
type ValidateEmailChallengeResponse = 
  | { outcome: 'challenged'; cart: Cart }
  | { outcome: 'notChallenged'; reason: 'errored' };

// Request body type that includes optional clientId
type ValidateEmailChallengeRequestBody = CheckoutV2.AuthValidateEmailChallengeRequestBody & {
  clientId?: string;
};

const MUTATION_VALIDATE_EMAIL_CHALLENGE_KEY = 'MUTATION/VALIDATE_EMAIL_CHALLENGE';

export const useValidateEmailChallenge = () => {
  const toast = useToast();
  const loginInfo = useROLoginAtom();
  const email = useROEmailAtom();
  const { formMessage } = useFormMessageAtom();
  const queryError = useQueryError({});
  const router = useCustomRouter();
  const [_isPrefilled, setIsPrefilled] = useAtom(isPrefilledAtom);
  const { close } = useShowChallengeAtom();
  const baseUrl = useBaseApiUrl();
  const { refetch } = useCart();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (values: ValidateEmailChallengeRequestBody) =>
      gateway.post<ValidateEmailChallengeResponse>(`${baseUrl}/validate-email-challenge`, {
        ...values,
        email,
      }),
    mutationKey: [MUTATION_VALIDATE_EMAIL_CHALLENGE_KEY],
    onError: async (error: any) => {
      const title = queryError(error);
      toast({
        description: title,
        isClosable: true,
        status: 'error',
        position: 'top',
        duration: 20000,
      });
    },
    onSuccess: async ({ data }) => {
      if (data.outcome !== 'challenged') {
        throw new Error('Failed to challenge');
      }
      
      // Update the cart cache with the returned cart data
      if (data.cart) {
        queryClient.setQueryData(['cart'], data.cart);
      } else {
        // Fallback to refetch if cart is not provided
        await refetch();
      }
      
      close();
      setIsPrefilled(false); // allow form to be filled again with data account
      router.push('/unauthenticated');
    },
  });

  const onValidateEmailChallenge = async (code: string) => {
    await mutation.mutateAsync({
      code: code.toString(),
      userId: loginInfo!.userId!,
      sessionId: loginInfo!.sessionId!,
      clientId: formMessage?.shopId ?? 'web',
    });
  };

  return {
    onValidateEmailChallenge,
    status: mutation.status,
  };
};
